const { getProducts } = require('../master-data')
const { listBatches: getBatches } = require('../../../batch/api/batch-list')
const { getAllocatedStock } = require('../read/get-allocated-stock')
const { getGroup } = require('../read/get')
const tools = require('../../tools')
const createBulkSnapshots = require('../../../shipment/shipment-bulk-create-snapshots')
const { createOrderId } = require('../../tools/write/snapshot-create')
const shortid = require('shortid')
const get = require('lodash/get')

exports.acceptLonghaulTransferOrders = async function (state, {
  groupId,
  dryRun = false,
  date = new Date().toJSON(),
  api
}) {
  const {orders} = await getGroup(state, { groupId, withSnapshots: true, useAcceptedStatus: true })
  const interwarehouseOrders = orders.filter(order => order.orderType === 'interwarehouse')

  if (!interwarehouseOrders.length) {
    return { acceptedSnapshotsUpdated: 0, shipmentSnapshots: [] }
  }

  const snapshots = interwarehouseOrders
    .map(order => get(order, ['snapshotsByStatus', order.status, '0'], null))
    .filter(Boolean)

  const programId = snapshots[0].programId

  const [productsList, batchesList] = await Promise.all([
    getProducts(state, programId),
    getBatches(state)
  ])

  const relevantBatches = batchesList.filter(batch => !batch.expiry || batch.expiry > date)

  const productsById = {}
  productsList.forEach(p => { productsById[p._id] = p })
  const batchesById = {}
  relevantBatches.forEach(b => { batchesById[b._id] = b })

  const snapshotsBySupplier = {}
  snapshots.forEach(s => {
    const supplier = s.supplierId
    if (!snapshotsBySupplier[supplier]) snapshotsBySupplier[supplier] = []
    snapshotsBySupplier[supplier].push(s)
  })

  const allAcceptedSnapshots = []
  const allShipmentSnapshots = []
  const user = state.user
  const timestamp = new Date().toJSON()

  for (const [supplierId, supplierSnapshots] of Object.entries(snapshotsBySupplier)) {
    const stockResponse = await getAllocatedStock(state, {
      programId,
      date,
      locationId: supplierId,
      includeLedger: true
    })
    const { ledgerSDPUnits, funderId } = stockResponse

    // The ledger is built from the program's service products, so it can have
    // entries for every product while holding zero quantity for the supplier.
    // Skip suppliers that carry no stock at all (empty ledger or all zeroes)
    // instead of failing batch validation on the first product.
    const hasStock = Object.values(ledgerSDPUnits || {})
      .some(product => (product.total || 0) > 0)
    if (!hasStock) {
      continue
    }

    const normalisedSnapshots = supplierSnapshots.map(snapshot => {
      const products = Object.keys(snapshot.products || {}).reduce((acc, productId) => {
        const product = snapshot.products[productId]
        const adjusted = product.adjusted || 0
        acc[productId] = { ...product, original: adjusted, adjusted }
        return acc
      }, {})
      return { ...snapshot, products }
    })

    const batchesApplied = tools.applyBatchesToSnapshots({
      ledger: ledgerSDPUnits,
      snapshots: normalisedSnapshots,
      productsById,
      batchesById,
      date
    })

    const acceptedSnapshots = batchesApplied.map(({ _id, _rev, ...snapshot }) => ({
      ...snapshot,
      _id: createOrderId({
        groupId: snapshot.groupId,
        orderId: snapshot.orderId,
        suborderId: shortid.generate()
      }),
      status: 'accepted',
      createdAt: timestamp,
      createdBy: user.name,
      updatedAt: timestamp,
      updatedBy: user.name
    }))

    allAcceptedSnapshots.push(...acceptedSnapshots)

    const rawShipmentSnapshots = tools.createShipmentSnapshots({
      acceptedSnapshots,
      funderId,
      productsList
    }).map(s => ({ ...s, removePaymentType: true }))

    const shipmentSnapshots = await api.shipment.decorateShipmentSnapshotsWithResolvedPrices(rawShipmentSnapshots)
    allShipmentSnapshots.push(...shipmentSnapshots)
  }

  if (dryRun) {
    return {
      acceptedSnapshotsUpdated: allAcceptedSnapshots.length,
      snapshots: allAcceptedSnapshots,
      shipmentSnapshots: allShipmentSnapshots
    }
  }

  await Promise.all(
    allAcceptedSnapshots.map(doc => state.dal.order.update(state, doc))
  )

  const shipments = await createBulkSnapshots(state, allShipmentSnapshots)

  return {
    acceptedSnapshotsUpdated: allAcceptedSnapshots.length,
    shipments
  }
}
